
/**
 * Utility functions for handling authentication-related operations
 */

/**
 * Clean up auth state to prevent authentication limbo
 */
export const cleanupAuthState = () => {
  // Remove standard auth tokens
  localStorage.removeItem('supabase.auth.token');
  
  // Remove all Supabase auth keys from localStorage
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  
  // Remove from sessionStorage if in use
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
};

/**
 * Safe sign-in helper that prevents database schema errors
 * This is a wrapper around the supabase auth signInWithPassword method
 */
export const safeSignIn = async (supabase, email, password) => {
  try {
    // Clean up any existing auth state first to prevent conflicts
    cleanupAuthState();
    
    // Attempt to sign out first (to clear any potential bad state)
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      // Ignore errors during signout, just continue
      console.log('Pre-signin signout error (non-critical):', err);
    }
    
    // Attempt the sign in with added error handling
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // If we get a schema error, we need to handle it specially
      if (error.message && (
          error.message.includes('querying schema') || 
          error.message.includes('converting NULL to string')
        )) {
        console.error('Database schema error during sign-in:', error);
        return { 
          error: {
            message: 'Sign-in service is temporarily unavailable. Please try again later.'
          }
        };
      }
      
      return { error };
    }

    return { data };
  } catch (error) {
    console.error('Sign in error:', error);
    return { 
      error: {
        message: error.message || 'An unexpected error occurred during sign-in'
      }
    };
  }
};
